import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';

import { Skeleton } from '../../shared/ui/Skeleton';

import s from './ResultCard.module.css';
import { getStudentResults, studentResultsKey, type StudentResultRow } from './results-api';

const BANDS: { label: string; get: (r: StudentResultRow) => number | null }[] = [
  { label: 'Listening', get: (r) => r.listening_band },
  { label: 'Reading', get: (r) => r.reading_band },
  { label: 'Writing', get: (r) => r.writing_band },
  { label: 'Speaking', get: (r) => r.speaking_band },
];

function fmt(v: number | null): string {
  return v === null ? '—' : v.toFixed(1);
}

/** Home card for the most-recent result. Shows the latest attempt's bands (real
 *  data via student_view_results) and links to the full results history. */
export function ResultCard(): JSX.Element {
  const query = useQuery({ queryKey: studentResultsKey, queryFn: getStudentResults });
  // Phase 175 — a question-type drill is not a result: its score is an
  // accuracy, not four bands. The first FULL attempt is the recent result.
  const latest = query.data?.find((row) => row.is_drill !== true);
  const ready = latest?.status === 'completed' && latest.published_at !== null;

  return (
    <div className={s.card}>
      <header className={s.head}>
        <span className={s.eyebrow}>Recent results</span>
        <Link to="/student/results" className={s.chip}>
          {(query.data?.length ?? 0) > 0 ? 'View all' : 'Pending grading'}
        </Link>
      </header>

      {query.isPending ? (
        <div className={s.loading} role="status" aria-label="Loading your latest result">
          <Skeleton width="60%" height={20} />
          <div className={s.bandRow}>
            {BANDS.map(({ label }) => (
              <Skeleton key={label} height={56} radius="md" />
            ))}
          </div>
        </div>
      ) : latest === undefined ? (
        <>
          <h2 className={s.title}>Your bands appear here once an attempt is reviewed</h2>
          <p className={s.body}>
            Listening and Reading bands publish automatically the moment you submit; Writing and
            Speaking bands appear after your tutor finishes grading and you’ll get a notification.
          </p>
        </>
      ) : (
        <>
          <h2 className={s.title}>
            {latest.test_title}
            {ready ? '' : ' — grading in progress'}
          </h2>
          <div className={s.bandRow}>
            {BANDS.map(({ label, get }) => (
              <div key={label} className={s.band}>
                <span className={s.bandLabel}>{label}</span>
                <span className={s.bandValue}>{fmt(get(latest))}</span>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
}
